import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  SearchBox,
  Select,
  Switch,
  Textarea,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowDownload20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Database20Regular,
  Delete20Regular,
  Dismiss24Regular,
  Edit20Regular,
} from "@fluentui/react-icons";
import styles from "./TaskManager.module.css";
import px from "./Taskdata.module.css";
import { api, crud, msgOf } from "./Api/Taskapi";
import type { Paged } from "./Api/Taskapi";
import { ENTITIES, SERVICE_LABELS } from "../../types/Taskentities";
import type { EntityInfo } from "../../types/Taskentities";
import { FormDialog, Head, Wait, downloadCsv, useAct, useLoad } from "./Taskpages";
import type { PageCtx } from "./Taskpages";

/* ------------------------------------------------------------------ *
 *  Data – every table of the Task Manager API in one place.
 *  The columns, the read-only flags and the required fields come from
 *  each table's /meta, so this screen always matches the server.
 * ------------------------------------------------------------------ */

interface Col {
  name: string;
  type: string;
  nullable: boolean;
  key: boolean;
  readOnly: boolean;
  requiredOnCreate: boolean;
}
interface Meta {
  table: string;
  scope: string;
  readOnly: boolean;
  adminWrite: boolean;
  search: string[];
  columns: Col[];
}
type Row = Record<string, unknown>;

const camel = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const isNum = (t: string) => ["Int64", "Int32", "Int16", "Decimal", "Double", "Single", "Byte"].includes(t);
const isText = (t: string) => t === "String";
const longText = (n: string) => /(json|body|description|notes|text|content|payload|message|reason|resolution|definition)/i.test(n);

const show = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
};
const toLocalInput = (v: unknown) => {
  if (typeof v !== "string" || !v) return "";
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${v}Z`);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** form text -> the value the API expects (undefined = leave it out) */
const parseField = (c: Col, text: string): unknown => {
  if (c.type === "Boolean") return text === "true";
  if (text.trim() === "") return c.nullable ? null : undefined;
  if (isNum(c.type)) {
    const n = Number(text);
    return Number.isNaN(n) ? undefined : n;
  }
  if (c.type === "DateTime") return new Date(text).toISOString();
  return text;
};
const toText = (c: Col, v: unknown): string => {
  if (v === null || v === undefined) return c.type === "Boolean" ? "false" : "";
  if (c.type === "DateTime") return toLocalInput(v);
  if (c.type === "DateOnly") return String(v).slice(0, 10);
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const RowForm: React.FC<{ meta: Meta; route: string; row: Row | null; onDone: () => void; ctx: PageCtx }> = ({ meta, route, row, onDone, ctx }) => {
  const cols = meta.columns.filter((c) => !c.readOnly && !c.key);
  const [vals, setVals] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    for (const c of cols) {
      const cur = row ? row[camel(c.name)] : undefined;
      o[c.name] = row ? toText(c, cur) : c.name === "WorkspaceId" ? String(ctx.workspaceId) : c.type === "Boolean" ? "false" : "";
    }
    return o;
  });
  const [busy, setBusy] = useState(false);
  const missing = cols.some((c) => !row && c.requiredOnCreate && (vals[c.name] ?? "").trim() === "" && c.type !== "Boolean");

  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = {};
      for (const c of cols) {
        const text = vals[c.name] ?? "";
        const before = row ? toText(c, row[camel(c.name)]) : "";
        if (row && text === before) continue; // only send what changed
        const v = parseField(c, text);
        if (v !== undefined) body[camel(c.name)] = v;
      }
      const id = row ? Number(row.id) : 0;
      if (row) await crud.update(route, id, body);
      else await crud.create(route, body);
      ctx.notify("success", row ? "Saved." : "Added.");
      onDone();
    } catch (e) {
      ctx.notify("error", msgOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogContent className={styles.formBody}>
        {cols.map((c) => {
          const v = vals[c.name] ?? "";
          const set = (x: string) => setVals((o) => ({ ...o, [c.name]: x }));
          const label = c.name.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
          const required = c.requiredOnCreate && !row;
          if (c.type === "Boolean")
            return (
              <Switch key={c.name} label={label} checked={v === "true"} onChange={(_, d) => set(d.checked ? "true" : "false")} />
            );
          if (isText(c.type) && longText(c.name))
            return (
              <Field key={c.name} label={label} required={required}>
                <Textarea value={v} rows={3} resize="vertical" onChange={(_, d) => set(d.value)} />
              </Field>
            );
          return (
            <Field key={c.name} label={label} required={required}>
              <Input
                value={v}
                type={isNum(c.type) ? "number" : c.type === "DateTime" ? "datetime-local" : c.type === "DateOnly" ? "date" : "text"}
                onChange={(_, d) => set(d.value)}
              />
            </Field>
          );
        })}
        {cols.length === 0 && <div className={styles.emptyRow}>Nothing here can be edited.</div>}
      </DialogContent>
      <div className={px.formFoot}>
        <DialogTrigger disableButtonEnhancement>
          <Button appearance="secondary">Cancel</Button>
        </DialogTrigger>
        <Button className={styles.primary} appearance="primary" disabled={busy || missing || cols.length === 0} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </>
  );
};

const PAGE_SIZES = [25, 50, 100, 200];

const EntityView: React.FC<{ info: EntityInfo; ctx: PageCtx }> = ({ info, ctx }) => {
  const act = useAct(ctx.notify);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [sort, setSort] = useState("");
  const [edit, setEdit] = useState<Row | "new" | null>(null);
  const [peek, setPeek] = useState<Row | null>(null);

  useEffect(() => {
    setQ("");
    setPage(1);
    setSort("");
    setEdit(null);
  }, [info.route]);

  const meta = useLoad(() => api<Meta>("GET", `/api/${info.route}/meta`), [info.route]);
  const list = useLoad(
    () => api<Paged<Row>>("GET", `/api/${info.route}`, undefined, { page, pageSize: size, q: q.trim() || undefined, sort: sort || undefined, total: true }),
    [info.route, page, size, q, sort],
  );
  const m = meta.data;
  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? null;
  const pages = total === null ? null : Math.max(1, Math.ceil(total / size));
  const canWrite = !!m && !m.readOnly && !info.readOnly;
  const cols = useMemo(() => (m ? m.columns.filter((c) => !["CreatedBy", "UpdatedBy", "UpdatedAt"].includes(c.name)).slice(0, 12) : []), [m]);

  const sortBy = (name: string) => setSort((s) => (s === name ? `-${name}` : s === `-${name}` ? "" : name));

  return (
    <div className={px.main}>
      <div className={px.mainHead}>
        <div>
          <h3 className={px.entityTitle}>{info.label}</h3>
          <div className={styles.muted}>
            {info.route}
            {m ? ` · ${m.scope.toLowerCase()} data` : ""}
            {m?.readOnly || info.readOnly ? " · read only" : m?.adminWrite ? " · admins can change" : ""}
          </div>
        </div>
        <div className={px.actions}>
          <SearchBox className={styles.search} placeholder={m && m.search.length ? `Search ${m.search.join(", ")}` : "Search"} value={q} onChange={(_, d) => (setQ(d.value), setPage(1))} />
          <Button
            icon={<ArrowDownload20Regular />}
            disabled={!m || rows.length === 0}
            onClick={() => m && downloadCsv(`${info.name.toLowerCase()}.csv`, cols.map((c) => c.name), rows.map((r) => cols.map((c) => r[camel(c.name)])))}
          >
            CSV
          </Button>
          {canWrite && (
            <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setEdit("new")}>
              Add
            </Button>
          )}
        </div>
      </div>

      <Wait loading={(meta.loading && !m) || (list.loading && !list.data)} error={meta.error || list.error} onRetry={() => (meta.reload(), list.reload())} />

      {m && list.data && (
        <>
          <div className={px.scroll}>
            <table className={px.tbl}>
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c.name}>
                      <button type="button" className={px.sortBtn} onClick={() => sortBy(c.name)} aria-label={`Sort by ${c.name}`}>
                        {c.name}
                        {sort === c.name ? " ▲" : sort === `-${c.name}` ? " ▼" : ""}
                      </button>
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={String(r.id ?? i)} className={px.row} onClick={() => setPeek(r)}>
                    {cols.map((c) => (
                      <td key={c.name} title={typeof r[camel(c.name)] === "string" ? String(r[camel(c.name)]) : undefined}>
                        {show(r[camel(c.name)])}
                      </td>
                    ))}
                    <td className={px.rowActions} onClick={(e) => e.stopPropagation()}>
                      {canWrite && (
                        <>
                          <Button size="small" appearance="subtle" icon={<Edit20Regular />} aria-label="Edit row" onClick={() => setEdit(r)} />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Delete20Regular />}
                            aria-label="Delete row"
                            onClick={async () => {
                              if (await act(() => crud.remove(info.route, Number(r.id)), "Deleted.")) list.reload();
                            }}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <div className={styles.emptyRow}>No rows</div>}
          </div>
          <div className={px.pager}>
            <span className={styles.muted}>{total === null ? `${rows.length} rows` : `${total} rows`}</span>
            <Select aria-label="Rows per page" value={String(size)} onChange={(_, d) => (setSize(Number(d.value)), setPage(1))}>
              {PAGE_SIZES.map((n) => (
                <option key={n} value={String(n)}>
                  {n} per page
                </option>
              ))}
            </Select>
            <Button size="small" icon={<ChevronLeft20Regular />} aria-label="Previous page" disabled={page <= 1} onClick={() => setPage(page - 1)} />
            <span>
              {page}
              {pages ? ` / ${pages}` : ""}
            </span>
            <Button size="small" icon={<ChevronRight20Regular />} aria-label="Next page" disabled={pages !== null ? page >= pages : rows.length < size} onClick={() => setPage(page + 1)} />
          </div>
        </>
      )}

      <Dialog open={edit !== null} onOpenChange={(_, o) => !o.open && setEdit(null)}>
        <DialogSurface className={`${styles.dialog} ${px.formSurface}`}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              {edit === "new" ? `Add to ${info.label}` : `Edit ${info.label} #${edit ? String(edit.id) : ""}`}
            </DialogTitle>
            {m && edit !== null && (
              <RowForm
                key={edit === "new" ? "new" : String(edit.id)}
                meta={m}
                route={info.route}
                row={edit === "new" ? null : edit}
                ctx={ctx}
                onDone={() => {
                  setEdit(null);
                  list.reload();
                }}
              />
            )}
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <FormDialog open={peek !== null} title="Row details" action="Close" onClose={() => setPeek(null)} onSubmit={() => setPeek(null)}>
        <pre className={px.json}>{peek ? JSON.stringify(peek, null, 2) : ""}</pre>
      </FormDialog>
    </div>
  );
};

export const DataPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [filter, setFilter] = useState("");
  const [pick, setPick] = useState<EntityInfo>(() => ENTITIES.find((e) => e.route === "tasks/tsk/task") ?? (ENTITIES[0] as EntityInfo));
  const groups = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const map = new Map<string, EntityInfo[]>();
    for (const e of ENTITIES) {
      if (f && !`${e.label} ${e.route} ${SERVICE_LABELS[e.service] ?? e.service}`.toLowerCase().includes(f)) continue;
      const list = map.get(e.service) ?? [];
      list.push(e);
      map.set(e.service, list);
    }
    return [...map.entries()];
  }, [filter]);

  return (
    <>
      <Head icon={<Database20Regular />} title="Data" sub={`All ${ENTITIES.length} tables of the Task Manager API: browse, search, add, edit and export.`} />
      <div className={styles.body}>
        <div className={px.split}>
          <nav className={px.side} aria-label="Tables">
            <SearchBox className={styles.search} placeholder="Find a table" value={filter} onChange={(_, d) => setFilter(d.value)} />
            {groups.map(([svc, list]) => (
              <div key={svc} className={px.group}>
                <div className={px.groupTitle}>{SERVICE_LABELS[svc] ?? svc}</div>
                {list.map((e) => (
                  <button key={e.route} type="button" className={`${px.tableBtn} ${pick.route === e.route ? px.tableOn : ""}`} onClick={() => setPick(e)}>
                    {e.label}
                  </button>
                ))}
              </div>
            ))}
            {groups.length === 0 && <div className={styles.emptyRow}>No table matches</div>}
          </nav>
          <EntityView info={pick} ctx={ctx} />
        </div>
      </div>
    </>
  );
};